import { useApiMutation } from "@gladis/api-client";
import { formatEur, type PropertyDetailDto } from "@gladis/shared";
import type { ReactNode } from "react";

import { formatValuationMonth, needsAnnualValuationPrompt } from "../estimate-display.js";
import { PROPERTIES_KEY } from "./PropertyList.js";
import { propertyDetailKey } from "./ValuationHistory.js";
import { errorSentence, useT } from "@gladis/ui/i18n";

/**
 * The indexed estimate: a hook for the control, and a notice for what the figure is.
 *
 * ==========================================================================================
 * THEY ARE SEPARATE BECAUSE THEY BELONG IN DIFFERENT PLACES ON THE PAGE
 * ==========================================================================================
 *
 * The control is one of the property's actions and sits in the title row with the others, where
 * every page in this application puts them. The notice is about the FIGURE, so it sits with the
 * figure — a reader meets "estimated since March 2024" beside the number it qualifies, not at the
 * top of the page beside a button.
 *
 * They were one component until the actions were gathered into the title row, and splitting them is
 * what let the button move without dragging the explanation along with it.
 *
 * ==========================================================================================
 * THE MARKER IS THE MORE IMPORTANT HALF
 * ==========================================================================================
 *
 * An indexed value is the household's own last real number moved by its region's market. It cannot
 * see a renovation, an extension or a new energy label, and it drifts further from reality the
 * longer the anchor goes unrefreshed.
 *
 * The failure mode is not a wrong number. It is a number that looks MAINTAINED while drifting years
 * from anything real — without this line, an estimate computed from a 2019 valuation renders exactly
 * like one a member entered this morning.
 */

export interface IndexedEstimateResult {
  value: string;
  valuationDate: string;
  anchor: { value: string; valuationDate: string };
  index: { region: string; atAnchor: string; now: string };
  /** False when the figure was already on record unchanged and nothing was written. */
  created: boolean;
}

/**
 * Derived from the hook rather than imported from `@tanstack/react-query`, which is not a dependency
 * of this package and should not become one — the query client is `@gladis/api-client`'s to own.
 */
export type UpdateEstimate = ReturnType<typeof useApiMutation<IndexedEstimateResult, void>>;

/**
 * Recomputes the estimate.
 *
 * Invalidates BOTH keys. The estimate changes this property's current value, which the list page's
 * household totals are built from — leaving `PROPERTIES_KEY` alone would show a fresh figure on the
 * detail view and a stale total one click away.
 */
export function useUpdateEstimate(propertyId: string) {
  return useApiMutation<IndexedEstimateResult, void>(
    (client) =>
      client.post<IndexedEstimateResult>(`/properties/${propertyId}/valuations/indexed`, {}),
    { invalidates: [propertyDetailKey(propertyId), PROPERTIES_KEY] },
  );
}

export interface EstimateNoticeProps {
  property: PropertyDetailDto["property"];
  /** The control's state, so the outcome is reported beside the figure it changed. */
  update: UpdateEstimate;
  /** Injected so the annual prompt is testable without waiting for February. */
  now?: Date;
}

export function EstimateNotice({ property, update, now }: EstimateNoticeProps): ReactNode {
  const t = useT();
  const today = now ?? new Date();
  const stale = property.estimatedSince;
  const result = update.data;

  if (
    stale === null &&
    !needsAnnualValuationPrompt(property, today) &&
    !result &&
    !update.isError
  ) {
    return null;
  }

  return (
    <div className="mb-4 flex flex-col gap-2">
      {stale !== null && (
        <p className="text-muted text-sm">
          {/*
           * Both halves in one sentence, because either alone misleads. The date without "estimated
           * since" reads as when the property was last looked at; "estimated" without the date hides
           * how far it has drifted.
           */}
          Last valued {formatValuationMonth(stale)} · estimated since
        </p>
      )}

      {needsAnnualValuationPrompt(property, today) && (
        <p className="text-muted text-sm">{t("prop.estimate.wozHint")}</p>
      )}

      {result && (
        /*
         * THE ARITHMETIC, NOT JUST THE ANSWER (AC-PR3).
         *
         * "Your house is now worth €548,800" is not a sentence to assert without showing where it
         * came from. A member who can see it derives from their own January 2024 valuation and a
         * regional index that moved 100.0 → 137.2 can judge whether to trust it.
         */
        <p className="text-muted text-sm">
          {result.created ? t("prop.estimate.updatedTo") : t("prop.estimate.unchangedAt")}
          {formatEur(Number(result.value))} — {formatEur(Number(result.anchor.value))} from{" "}
          {formatValuationMonth(result.anchor.valuationDate)}, indexed {result.index.atAnchor} →{" "}
          {result.index.now} for {result.index.region}.
        </p>
      )}

      {update.isError && (
        /*
         * THE SERVER'S OWN WORDS FOR ANYTHING THIS PROPERTY CAN CAUSE.
         *
         * A 4xx is a fact about this property and is already phrased for a reader — "there is nothing
         * to carry forward yet". A 5xx is a fact about the world and its message is not. One
         * hardcoded sentence for both reported a refusal as somebody else's outage.
         */
        <p className="text-danger text-sm">
          {update.error.status < 500 ? errorSentence(update.error) : t("prop.estimate.unavailable")}
        </p>
      )}
    </div>
  );
}
